import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { PDFDocumentProxy, PDFProgressData, PDFSource, PdfViewerComponent } from 'ng2-pdf-viewer';
import { ElementClassName, Info } from '../../constant/information.const';

declare global {
  interface Window {
    pdfWorkerSrc: string;
  }
}

const assetsUrl = (url: string, postfixIfPathIsRelativ = ''): string => {
  if (url.includes('://')) {
    // the assets folder is on an absolute path (like https://example.com/assets)
    return url;
  }
  return `./${url + postfixIfPathIsRelativ}`;
};

/**
 *
 * class PdfEmbedComponent
 */
@Component({
  selector: 'ecp-pdf-embed',
  styleUrls: ['./pdf-embed.component.scss'],
  templateUrl: './pdf-embed.component.html'
})
export class PdfEmbedComponent {
  /** pdf source */
  @Input() public pdfSrc: string | Uint8Array | PDFSource;
  /** event trigger load complete */
  @Output() public pdfLoadComplete = new EventEmitter();
  /** holds error message */
  public error: string;
  /** page number of current page */
  public page = 1;
  /** angle of rotation */
  public rotation = 0;
  /** zoom level */
  public zoom = 0.9;
  /** to view doc in original size */
  public originalSize = false;
  /** pdf that  */
  public pdf: PDFDocumentProxy;
  /** render text or not */
  public renderText = true;
  /** progress data model */
  public progressData: PDFProgressData;
  /** indicate document is loaded or not */
  public isLoaded = false;
  /** config: stick to page */
  public stickToPage = false;
  /** show all page */
  public showAll = true;
  /** auto resize document */
  public autoresize = true;
  /** fit document to page */
  public fitToPage = false;
  /** outline */
  public outline: [];
  /** pdf viewer component */
  @ViewChild(PdfViewerComponent) private readonly pdfComponent: PdfViewerComponent;

  constructor() {
    window.pdfWorkerSrc = this.workerSrc;
  }

  get workerSrc(): string {
    return `${assetsUrl('assets')}/pdf.worker.min.mjs`;
  }

  public incrementPage(amount: number): void {
    this.page += amount;
  }

  public incrementZoom(amount: number): void {
    this.zoom += amount;
  }

  public rotate(angle: number): void {
    this.rotation += angle;
  }

  /**
   * Get pdf information after it's loaded
   */
  public afterLoadComplete(pdf: PDFDocumentProxy): void {
    const timeout = 100;
    this.pdf = pdf;
    this.isLoaded = true;

    this.loadOutline();
    setTimeout(() => {
      this.pdfLoadComplete.emit(true);
    }, timeout);
  }

  /**
   * Get outline
   */
  public loadOutline(): void {
    this.pdf.getOutline().then((outline: []) => {
      this.outline = outline;
    });
  }

  /**
   * Handle error callback
   *
   */
  public onError(error): void {
    this.error = error; // set error

    if (error.name === Info.passwordException) {
      const password = prompt(Info.passwordErrorMessage);

      if (password) {
        this.error = undefined;
      }
    }
  }

  /**
   * Pdf loading progress callback
   */
  public onProgress(progressData: PDFProgressData): void {
    this.progressData = progressData;
    this.isLoaded = false;
    this.error = undefined; // clear error
  }

  public getInt(value: number): number {
    return Math.round(value);
  }

  /**
   * Navigate to destination
   */
  public navigateTo(destination): void {
    this.pdfComponent.pdfLinkService.goToDestination(destination); // Changed this from navigateTo as it is no longer present.
  }

  /**
   * Scroll view
   */
  public scrollToPage(): void {
    this.pdfComponent.pdfViewer.scrollPageIntoView({
      pageNumber: 3
    });
  }

  /**
   * Page rendered callback, which is called when a page is rendered (called multiple times)
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public pageRendered(e: CustomEvent): void {
    this.removeScrollX();
  }

  private removeScrollX(): void {
    const element = document.getElementsByClassName(ElementClassName.pdfViewerContainer);
    if (element && element.length > 0) {
      element[0].setAttribute(Info.style, Info.pdfContainerStyle);
    }
  }
}
